// Centralized clock state management
export interface ClockState {
  isClockedIn: boolean;
  workStartTime: Date | null;
  isOnBreak: boolean;
  breakStartTime: Date | null;
  isOnLunch: boolean;
  lunchStartTime: Date | null;
  hasPhotoProof: boolean;
  clockInPhoto: string | null;
  clockOutPhoto: string | null;
}

export const defaultClockState: ClockState = {
  // For testing - user starts clocked in
  isClockedIn: true,
  workStartTime: new Date(Date.now() - 4.5 * 60 * 60 * 1000), // 4.5 hours ago
  isOnBreak: false,
  breakStartTime: null,
  isOnLunch: false,
  lunchStartTime: null,
  hasPhotoProof: true,
  clockInPhoto: null,
  clockOutPhoto: null,
};

const STORAGE_KEY = 'clockit-clock-state';

export class ClockStateManager {
  private static instance: ClockStateManager;
  private state: ClockState;
  private listeners: Set<(state: ClockState) => void> = new Set();

  private constructor() {
    this.state = this.loadState();
  }

  static getInstance(): ClockStateManager {
    if (!ClockStateManager.instance) {
      ClockStateManager.instance = new ClockStateManager();
    }
    return ClockStateManager.instance;
  }

  private loadState(): ClockState {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          workStartTime: parsed.workStartTime ? new Date(parsed.workStartTime) : null,
          breakStartTime: parsed.breakStartTime ? new Date(parsed.breakStartTime) : null,
          lunchStartTime: parsed.lunchStartTime ? new Date(parsed.lunchStartTime) : null,
        };
      }
    } catch (error) {
      console.error('Failed to load clock state:', error);
    }
    return { ...defaultClockState };
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      console.error('Failed to save clock state:', error);
    }
  }

  getState(): ClockState {
    return { ...this.state };
  }

  setState(newState: Partial<ClockState>): void {
    this.state = { ...this.state, ...newState };
    this.saveState();
    this.notifyListeners();
  }

  subscribe(listener: (state: ClockState) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  // Action methods with enhanced feedback
  clockIn(withPhoto?: string): void {
    console.log('🟢 Clock In - Starting work shift');
    this.setState({
      isClockedIn: true,
      workStartTime: new Date(),
      hasPhotoProof: !!withPhoto,
      clockInPhoto: withPhoto || null,
      isOnBreak: false,
      breakStartTime: null,
      isOnLunch: false,
      lunchStartTime: null,
    });
    this.showToast('Clocked in successfully! 🟢', 'success');
  }

  clockOut(withPhoto?: string): void {
    const workedHours = this.getWorkedHours();
    console.log(`🔴 Clock Out - Worked ${workedHours.toFixed(1)} hours`);
    this.setState({
      isClockedIn: false,
      workStartTime: null,
      clockOutPhoto: withPhoto || null,
      isOnBreak: false,
      breakStartTime: null,
      isOnLunch: false,
      lunchStartTime: null,
    });
    this.showToast(`Clocked out! Worked ${workedHours.toFixed(1)} hours today 🎯`, 'success');
  }

  startBreak(): void {
    if (!this.state.isClockedIn) {
      this.showToast('You must be clocked in to take a break', 'warning');
      return;
    }
    if (this.state.isOnLunch) {
      this.showToast('End your lunch break first', 'warning');
      return;
    }
    console.log('☕ Break Started');
    this.setState({
      isOnBreak: true,
      breakStartTime: new Date(),
      isOnLunch: false,
      lunchStartTime: null,
    });
    this.showToast('Break started! Take your time ☕', 'info');
  }

  endBreak(): void {
    const breakDuration = this.getBreakDuration();
    console.log(`☕ Break Ended - Duration: ${Math.floor(breakDuration)} minutes`);
    this.setState({
      isOnBreak: false,
      breakStartTime: null,
    });
    this.showToast(`Break ended! Took ${Math.floor(breakDuration)} minutes ⚡`, 'success');
  }

  startLunch(): void {
    if (!this.state.isClockedIn) {
      this.showToast('You must be clocked in to take lunch', 'warning');
      return;
    }
    if (this.state.isOnBreak) {
      this.showToast('End your break first', 'warning');
      return;
    }
    console.log('🍽️ Lunch Started');
    this.setState({
      isOnLunch: true,
      lunchStartTime: new Date(),
      isOnBreak: false,
      breakStartTime: null,
    });
    this.showToast('Lunch started! Enjoy your meal 🍽️', 'info');
  }

  endLunch(): void {
    const lunchDuration = this.getLunchDuration();
    console.log(`🍽️ Lunch Ended - Duration: ${Math.floor(lunchDuration)} minutes`);
    this.setState({
      isOnLunch: false,
      lunchStartTime: null,
    });
    this.showToast(`Lunch ended! Took ${Math.floor(lunchDuration)} minutes 🍽️`, 'success');
  }

  // Add photo to existing clock-in session
  addPhotoToCurrentSession(photoUrl: string): void {
    if (!this.state.isClockedIn) {
      this.showToast('You must be clocked in to add photo proof', 'warning');
      return;
    }
    
    console.log('📸 Adding photo to current session');
    this.setState({
      hasPhotoProof: true,
      clockInPhoto: photoUrl
    });
    this.showToast('Photo added! Your records are now strengthened 📸✨', 'success');
  }

  // Helper method for toast notifications
  private showToast(message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info'): void {
    // Create a custom event that the NotificationSystem can listen for
    const event = new CustomEvent('clockit-toast', {
      detail: { message, type, timestamp: new Date() }
    });
    window.dispatchEvent(event);
    
    // Add haptic feedback on mobile devices
    this.triggerHapticFeedback(type);
  }

  // Helper method for haptic feedback
  private triggerHapticFeedback(type: 'success' | 'info' | 'warning' | 'error'): void {
    if ('vibrate' in navigator && navigator.vibrate) {
      switch (type) {
        case 'success':
          navigator.vibrate([50, 50, 100]); // Success pattern
          break;
        case 'warning':
        case 'error':
          navigator.vibrate([100, 100, 100]); // Warning pattern
          break;
        case 'info':
        default:
          navigator.vibrate(50); // Simple feedback
          break;
      }
    }
  }

  getWorkedHours(): number {
    if (!this.state.workStartTime) return 0;
    const now = new Date();
    const diffMs = now.getTime() - this.state.workStartTime.getTime();
    return diffMs / (1000 * 60 * 60);
  }

  getBreakDuration(): number {
    if (!this.state.breakStartTime) return 0;
    const now = new Date();
    const diffMs = now.getTime() - this.state.breakStartTime.getTime();
    return diffMs / (1000 * 60);
  }

  getLunchDuration(): number {
    if (!this.state.lunchStartTime) return 0;
    const now = new Date();
    const diffMs = now.getTime() - this.state.lunchStartTime.getTime();
    return diffMs / (1000 * 60);
  }
}

export const clockStateManager = ClockStateManager.getInstance();